"use strict";
/**
 * Workflow edge ports — which output port of a branch or loop step an edge
 * leaves from, and the visible label of a branch route.
 *
 * Shared by the editor's graph serializer and the canvas graph projection so
 * both resolve edges without a stored port the same way.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.withNamedSourcePorts = withNamedSourcePorts;
exports.buildBranchEdgeAssignments = buildBranchEdgeAssignments;
exports.buildLoopEdgeAssignments = buildLoopEdgeAssignments;
exports.buildConditionLabels = buildConditionLabels;
exports.extractBranchLayoutHints = extractBranchLayoutHints;
const control_flow_1 = require("./control-flow");
/**
 * The edges with the port each leaves from named. An edge that gives only the
 * number of its output, as runner definitions do, takes the port of that
 * number: a route of a branch, `continue` or `exit` of a loop.
 */
function withNamedSourcePorts(steps, edges) {
    const stepsById = new Map(steps.map(step => [step['id'], step]));
    return edges.map(edge => {
        const index = edge['sourceOutputIndex'];
        if (typeof edge['sourcePort'] === 'string' || typeof index !== 'number') {
            return edge;
        }
        const source = stepsById.get(edge['sourceStepId'] ?? edge['source']);
        const sourcePort = source ? indexedSourcePort(source, index) : undefined;
        return sourcePort === undefined ? edge : { ...edge, sourcePort };
    });
}
function indexedSourcePort(step, index) {
    const type = step['type'];
    if (type === 'loop') {
        return index === 0 ? 'continue' : index === 1 ? 'exit' : undefined;
    }
    if (typeof type !== 'string' || !control_flow_1.CONTROL_BRANCH_TYPES.has(type)) {
        return undefined;
    }
    const config = step['config'];
    const ports = isObject(config) ? config['ports'] : undefined;
    const port = Array.isArray(ports) ? ports[index] : undefined;
    const portId = isObject(port) ? port['portId'] : undefined;
    return typeof portId === 'string' ? portId : undefined;
}
function isObject(value) {
    return typeof value === 'object' && value !== null;
}
function buildBranchEdgeAssignments(definition) {
    const assignments = new Map();
    const stepsById = new Map(definition.steps.map(step => [step.id, step]));
    const edgesBySource = new Map();
    for (const edge of definition.edges) {
        const edges = edgesBySource.get(edge.sourceStepId) ?? [];
        edges.push(edge);
        edgesBySource.set(edge.sourceStepId, edges);
    }
    for (const step of definition.steps) {
        const layoutHints = extractBranchLayoutHints(step);
        if (!layoutHints?.branchPorts.length)
            continue;
        const ports = layoutHints.branchPorts;
        const usedPorts = new Set();
        const pending = [];
        for (const edge of edgesBySource.get(step.id) ?? []) {
            const explicitPort = edge.sourcePort;
            const explicitPortIndex = explicitPort != null ? ports.indexOf(explicitPort) : -1;
            if (explicitPort != null && explicitPortIndex >= 0) {
                assignments.set(edge.id, {
                    sourcePort: explicitPort,
                    sourceOutputIndex: explicitPortIndex,
                });
                usedPorts.add(explicitPort);
                continue;
            }
            const indexedPort = typeof edge.sourceOutputIndex === 'number'
                ? ports[edge.sourceOutputIndex]
                : undefined;
            if (indexedPort) {
                assignments.set(edge.id, {
                    sourcePort: indexedPort,
                    sourceOutputIndex: edge.sourceOutputIndex,
                });
                usedPorts.add(indexedPort);
                continue;
            }
            pending.push(edge);
        }
        const orderedPending = orderEdgesByTargetY(pending, stepsById);
        for (const edge of orderedPending) {
            const sourcePort = ports.find(port => !usedPorts.has(port));
            if (!sourcePort)
                continue;
            usedPorts.add(sourcePort);
            assignments.set(edge.id, {
                sourcePort,
                sourceOutputIndex: ports.indexOf(sourcePort),
            });
        }
    }
    return assignments;
}
function buildLoopEdgeAssignments(definition) {
    const assignments = new Map();
    const edgesBySource = new Map();
    for (const edge of definition.edges) {
        const edges = edgesBySource.get(edge.sourceStepId) ?? [];
        edges.push(edge);
        edgesBySource.set(edge.sourceStepId, edges);
    }
    for (const step of definition.steps) {
        if (step.type !== 'loop')
            continue;
        const outgoing = edgesBySource.get(step.id) ?? [];
        for (const edge of outgoing) {
            const explicitAssignment = resolveLoopEdgeAssignment(edge, step.id);
            if (explicitAssignment) {
                assignments.set(edge.id, explicitAssignment);
            }
        }
        const hasContinueEdge = outgoing.some(edge => assignments.get(edge.id)?.sourcePort === 'continue');
        const hasExitEdge = outgoing.some(edge => assignments.get(edge.id)?.sourcePort === 'exit');
        if (!hasContinueEdge || hasExitEdge)
            continue;
        const unlabeledNonSelfEdges = outgoing.filter(edge => !assignments.has(edge.id) && edge.targetStepId !== step.id);
        if (unlabeledNonSelfEdges.length !== 1)
            continue;
        assignments.set(unlabeledNonSelfEdges[0].id, {
            sourcePort: 'exit',
            sourceOutputIndex: 1,
        });
    }
    return assignments;
}
function resolveLoopEdgeAssignment(edge, loopStepId) {
    if (edge.sourcePort === 'continue') {
        return { sourcePort: 'continue', sourceOutputIndex: 0 };
    }
    if (edge.sourcePort === 'exit') {
        return { sourcePort: 'exit', sourceOutputIndex: 1 };
    }
    if (edge.sourceOutputIndex === 0) {
        return { sourcePort: 'continue', sourceOutputIndex: 0 };
    }
    if (edge.sourceOutputIndex === 1) {
        return { sourcePort: 'exit', sourceOutputIndex: 1 };
    }
    if (edge.targetStepId === loopStepId) {
        return { sourcePort: 'continue', sourceOutputIndex: 0 };
    }
    return undefined;
}
function orderEdgesByTargetY(edges, stepsById) {
    const indexed = edges.map((edge, index) => ({
        edge,
        index,
        targetY: stepsById.get(edge.targetStepId)?.position?.y,
    }));
    const finiteYs = indexed
        .map(item => item.targetY)
        .filter((value) => Number.isFinite(value));
    if (finiteYs.length < 2 || Math.max(...finiteYs) === Math.min(...finiteYs)) {
        return edges.map(edge => edge);
    }
    return indexed
        .sort((a, b) => {
        const aY = Number.isFinite(a.targetY)
            ? a.targetY
            : Number.MAX_SAFE_INTEGER;
        const bY = Number.isFinite(b.targetY)
            ? b.targetY
            : Number.MAX_SAFE_INTEGER;
        return aY - bY || a.index - b.index;
    })
        .map(item => item.edge);
}
/**
 * sl-tj3g: Build a map of branch edge labels from port conditions.
 * Key: "stepId:portId", Value: human-readable condition string.
 */
function buildConditionLabels(definition) {
    const labels = new Map();
    for (const step of definition.steps) {
        if (!control_flow_1.CONTROL_BRANCH_TYPES.has(step.type))
            continue;
        const ports = step.config['ports'];
        if (!ports)
            continue;
        for (const port of ports) {
            const cond = port.condition;
            let label = port.label ?? port.portId;
            if (cond?.expression && cond?.operator) {
                // sl-o3bm audit M12: Support unary operators (exists, isEmpty)
                if (cond.operator === 'exists') {
                    label = `${cond.expression} exists`;
                }
                else if (cond.operator === 'isEmpty') {
                    label = `${cond.expression} is empty`;
                }
                else {
                    const op = cond.operator === 'eq'
                        ? '=='
                        : cond.operator === 'neq'
                            ? '!='
                            : cond.operator === 'gt'
                                ? '>'
                                : cond.operator === 'gte'
                                    ? '>='
                                    : cond.operator === 'lt'
                                        ? '<'
                                        : cond.operator === 'lte'
                                            ? '<='
                                            : cond.operator;
                    const val = cond.value !== undefined ? ` ${cond.value}` : '';
                    label = `${cond.expression} ${op}${val}`;
                }
            }
            labels.set(`${step.id}:${port.portId}`, label);
        }
    }
    return labels;
}
/**
 * sl-z0uv.1: Extract layout hints from branch step config.
 * Returns pairedMergeId and branchPorts for layout engine pair detection.
 */
function extractBranchLayoutHints(step) {
    if (!control_flow_1.CONTROL_BRANCH_TYPES.has(step.type))
        return undefined;
    const pairedMergeId = step.config['pairedMergeId'];
    if (!pairedMergeId)
        return undefined;
    const ports = step.config['ports'];
    const branchPorts = ports?.map(p => p.portId) ?? [];
    return { pairedMergeId, branchPorts };
}
